<template>
  <div class="dashboard">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-row">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon" style="background: #409EFF;">
            <i class="el-icon-user"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.userCount || 0 }}</div>
            <div class="stat-label">{{ $t('dashboard.userCount') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon" style="background: #67C23A;">
            <i class="el-icon-s-promotion"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.pushCount || 0 }}</div>
            <div class="stat-label">{{ $t('dashboard.pushCount') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon" style="background: #E6A23C;">
            <i class="el-icon-document"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.contentCount || 0 }}</div>
            <div class="stat-label">{{ $t('dashboard.contentCount') }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon" style="background: #F56C6C;">
            <i class="el-icon-headset"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.recordCount || 0 }}</div>
            <div class="stat-label">{{ $t('dashboard.recordCount') }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 系统设置 -->
    <el-card class="settings-card">
      <div slot="header" class="card-header">
        <span>{{ $t('dashboard.settings') }}</span>
        <span style="float: right; font-size: 12px; color: #909399;">
          {{ $t('dashboard.weekInfo', { week: stats.currentWeek || 1, start: stats.startDate || '-', lastUpdate: stats.lastUpdate || '-' }) }}
        </span>
      </div>
      <el-form label-width="120px" inline>
        <el-form-item :label="$t('dashboard.weekStart')">
          <el-date-picker
            v-model="weekStartDate"
            type="date"
            :placeholder="$t('dashboard.weekStartPlaceholder')"
            value-format="yyyy-MM-dd"
            @change="handleDateUpdate"
          ></el-date-picker>
        </el-form-item>
        <!-- 当前周数由起始日期自动计算（与用户入组周同一口径），只读展示，不提供手动修改。
             这里不使用 el-form-item 的 label 属性：它会把标签固定成 label-width(120px) 宽，
             标签文字与数值之间会空出一段。改为把标签文字放进内容区，数值即可紧贴标签显示。 -->
        <el-form-item class="auto-week-item">
          <span class="auto-week-label">{{ $t('dashboard.autoWeek') }}</span>
          <span class="auto-week">
            <span class="auto-week-value">{{ stats.currentWeek ?? 1 }}</span>
            <span class="auto-week-unit">{{ $t('dashboard.weekUnit') }}</span>
          </span>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 批次周概览 -->
    <el-card class="batch-card">
      <div slot="header" class="card-header">
        <span>{{ $t('dashboard.batchOverview') }}</span>
      </div>
      <el-table :data="pagedBatchWeeks" border stripe>
        <el-table-column prop="id" :label="$t('dashboard.colId')" width="80"></el-table-column>
        <el-table-column prop="name" :label="$t('dashboard.colName')"></el-table-column>
        <el-table-column prop="start_date" :label="$t('dashboard.colStart')" width="120"></el-table-column>
        <el-table-column prop="total_weeks" :label="$t('dashboard.colTotalWeeks')" width="80"></el-table-column>
        <el-table-column prop="current_week" :label="$t('dashboard.colCurrentWeek')" width="100"></el-table-column>
        <el-table-column prop="member_count" :label="$t('dashboard.colMembers')" width="80"></el-table-column>
      </el-table>
      <!-- 只有一页时不渲染分页条，避免表格下方出现无意义的翻页控件 -->
      <div v-if="showBatchPagination" class="pagination">
        <el-pagination
          @current-change="handleBatchPageChange"
          :current-page="batchPage"
          :page-size="batchPageSize"
          :total="batchWeeks.length"
          layout="total, prev, pager, next"
          background
        ></el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getStats, updateWeek } from '@/api/dashboard'

export default {
  name: 'Dashboard',
  data() {
    return {
      stats: {},
      weekStartDate: '',
      batchWeeks: [],
      batchPage: 1,
      batchPageSize: 10
    }
  },
  created() {
    this.loadData()
  },
  computed: {
    // 批次周概览分页后的数据
    pagedBatchWeeks() {
      const start = (this.batchPage - 1) * this.batchPageSize
      return this.batchWeeks.slice(start, start + this.batchPageSize)
    },
    // 批次周概览总页数
    batchTotalPages() {
      return Math.ceil(this.batchWeeks.length / this.batchPageSize)
    },
    // 只有一页时不显示分页条。
    // 注意阈值用 >1 而不是 >2：若恰好两页也隐藏，第 2 页的数据将无法访问。
    showBatchPagination() {
      return this.batchTotalPages > 1
    }
  },
  methods: {
    // 加载仪表盘数据
    loadData() {
      getStats().then(res => {
        const data = res.data
        this.stats = data
        this.weekStartDate = data.startDate || ''
        this.batchWeeks = data.batchWeeks || []
        this.batchPage = 1
      }).catch(() => {
        this.$message.error(this.$t('dashboard.loadFail'))
      })
    },
    // 批次周概览翻页
    handleBatchPageChange(page) {
      this.batchPage = page
    },
    // 只保留「周起始日期」这一个入口：保存后由后端按该日期实时推算当前周数，
    // 与用户入组周的计算口径一致（距起始日期每满 7 天进一周），不再需要手动填周数。
    handleDateUpdate(val) {
      if (!val) return
      updateWeek({ type: 'date', start_date: val }).then(() => {
        this.$message.success(this.$t('dashboard.dateUpdated'))
        this.loadData()
      }).catch(() => {
        this.$message.error(this.$t('dashboard.updateFail'))
      })
    }
  }
}
</script>

<style scoped>
.stat-row {
  margin-bottom: 20px;
}
.stat-card .el-card__body {
  display: flex;
  align-items: center;
}
.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;
}
.stat-icon i {
  font-size: 28px;
  color: #fff;
}
.stat-info {
  flex: 1;
}
.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}
.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 5px;
}
.settings-card {
  margin-bottom: 20px;
}
.card-header {
  font-weight: bold;
  font-size: 16px;
}
/* 自动计算的当前周数（只读展示，视觉上比普通文本更醒目）。
   用 inline-flex + align-items: baseline 让 24px 的数字与 14px 的"周"按基线对齐；
   直接内联两个不同字号的 span 会因为行盒高度不一致而看起来偏下。 */
.auto-week-label {
  font-size: 14px;
  color: #606266;   /* 与 Element UI 默认表单标签同色，保持一行内视觉一致 */
  margin-right: 8px;
}
.auto-week {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}
.auto-week-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
}
.auto-week-unit {
  font-size: 14px;
  color: #606266;
}
.batch-card {
  margin-bottom: 20px;
}
.pagination {
  text-align: right;
  margin-top: 15px;
}
</style>
